/* eslint-disable react/prop-types */
import {Modal, Tabs} from "antd";
import React, {useCallback} from "react";
import styles from '../Style/NewPopup.module.scss';
import {MarkerItem} from "../../../../types/home";
import {UserGroupPermissions} from "../../../../types/userGroupPermissions";
import AnalyticsPanel from "../../Analytics/components/AnalyticsPanel";
import ValidationPanel from "../../Validation/ValidationPanel";
import {ITEM, TabKey} from "../../../../utils/common";

interface NewPopupModalProps {
  activeTab: TabKey;
  setActiveTab: (key: TabKey) => void
  isPopupVisible: boolean;
  setIsPopupVisible: (visible: boolean) => void;
  switchVisiblePopup: (id: string) => void
  activePoint?: MarkerItem;
  setDatasetIsOfflineWarningVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onMeshControl: (value: string) => any;
  isTnw: boolean;
  point?: MarkerItem;
  gridCellSize: string;
  waterDepth: string;
  userGroupPermissions: UserGroupPermissions;
  category: string | string[];
  activePlotValidation: string[];
  setActivePlotValidation: React.Dispatch<React.SetStateAction<string[]>>;
  onChangePointLocation: (coords: number[], forceChange?: boolean) => void;
  onChangeActiveKey: (item: string | string[], tabKey: TabKey) => void
  setShowLogin: Function
  isAuthorized: boolean
}

interface TabContentProps {
  tabKey: TabKey;
  datasetId: string;
  activePoint?: MarkerItem;
  setDatasetIsOfflineWarningVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onMeshControl: (value: string) => any;
  isTnw: boolean;
  point?: MarkerItem;
  gridCellSize: string;
  waterDepth: string;
  userGroupPermissions: UserGroupPermissions;
  category: string | string[];
  activePlotValidation: string[];
  setActivePlotValidation: React.Dispatch<React.SetStateAction<string[]>>;
  onChangePointLocation: (coords: number[], forceChange?: boolean) => void;
  onChangeActiveKey: (item: string | string[], key: TabKey) => void
}

const TabContentMemoized: React.FC<TabContentProps> = React.memo(function TabContent({
                                                            tabKey,
                                                            activePoint,
                                                            setDatasetIsOfflineWarningVisible,
                                                            onMeshControl,
                                                            datasetId,
                                                            point,
                                                            gridCellSize,
                                                            waterDepth,
                                                            userGroupPermissions,
                                                            category,
                                                            activePlotValidation,
                                                            setActivePlotValidation,
                                                            onChangePointLocation,
                                                            onChangeActiveKey
                                                          }){
  switch (tabKey) {
    case ITEM.VALIDATION:
      return (
        <ValidationPanel
          datasetId={datasetId}
          key={`validation-panel-${activePoint?.id}`}
          category={category as string}
          activePlotValidation={activePlotValidation}
          setActivePlotValidation={setActivePlotValidation}
          activePoint={activePoint}
          cellSize={gridCellSize}
          waterDepth={waterDepth}
          userGroupPermissions={userGroupPermissions}
          onChangePointLocation={onChangePointLocation}
          onMeshControl={onMeshControl}
          onChangeActiveKey={onChangeActiveKey}
        />
      )
    case ITEM.ANALYTICS:
      return (
        <AnalyticsPanel
          category={category}
          key={`analytics-panel-${activePoint?.id}`}
          downloadItems={activePoint?.downloadPage!}
          coordinates={activePoint?.coordinates!}
          previewDatasetId={datasetId}
          userGroupPermissions={userGroupPermissions}
          setDatasetIsOfflineWarningVisible={setDatasetIsOfflineWarningVisible}
          pointProps={
            activePoint?.coordinates[0] +
            '_' +
            activePoint?.coordinates[1] +
            '_' +
            waterDepth +
            '_' +
            gridCellSize
          }
          pointName={activePoint ? activePoint.label : ''}
          gridCellSize={gridCellSize.toString()!}
          waterDepth={waterDepth.toString()!}
          onMeshControl={onMeshControl}
          point={point}
          onChangeActiveKey={onChangeActiveKey}

        />
      )
    case ITEM.REPORTS:
      return <></>;
    default:
      return null;
  }
});

export const NewPopupModal: React.FC<NewPopupModalProps> = React.memo(function BaseNewPopupModal({
                                                                         isPopupVisible,
                                                                         setIsPopupVisible,
                                                                         activePoint,
                                                                         setDatasetIsOfflineWarningVisible,
                                                                         onMeshControl,
                                                                         isTnw,
                                                                         point,
                                                                         gridCellSize,
                                                                         waterDepth,
                                                                         userGroupPermissions,
                                                                         category,
                                                                         activePlotValidation,
                                                                         setActivePlotValidation,
                                                                         onChangePointLocation,
                                                                         activeTab,
                                                                         setActiveTab,
                                                                         onChangeActiveKey,
                                                                         switchVisiblePopup,
                                                                         setShowLogin,
                                                                         isAuthorized
                                                                       }) {
  const datasetId = activePoint?.downloadPage.find((item) => item.radioKey === category as string)?.datasetId ?? ''

  const handleTabChange = useCallback((key: string) => {
    if (key === ITEM.VALIDATION && !isAuthorized) {
      setShowLogin(true)
      return
    }
    setActiveTab(key as TabKey);
  }, [isAuthorized, setActiveTab, setShowLogin]);

  const handleCancel = useCallback(() => setIsPopupVisible(false), [setIsPopupVisible]);

  const handleChangePoint = (item: any, tabKey: TabKey) => {
    onChangeActiveKey(item, tabKey)
    switchVisiblePopup(item)
  }

  return (
    <Modal
      key={`validation-analytics-popup-${point?.id}`}
      open={isPopupVisible}
      onCancel={handleCancel}
      style={{
        paddingBottom: 0,
        top: 32,
        maxHeight: 'calc(100vh - 64px)',
        minWidth: 1200
      }}
      width={'96.5vw'}
      footer={[<></>]}
      className={styles.analyticsModal}
      title={<Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        className={styles.modalTabs}
        items={[
          {
            key: ITEM.VALIDATION,
            label: 'Validation',
          },
          {
            key: ITEM.ANALYTICS,
            label: 'Analytics',
          },
          // {
          //   key: ITEM.REPORTS,
          //   label: 'Reports',
          //   disabled: true
          // }
        ]}
      />}
    >
      <div className={styles.tabContent}>
        <TabContentMemoized
          key={`validation-analytics-content-${point?.id}`}
          onChangeActiveKey={handleChangePoint}
          activePlotValidation={activePlotValidation}
          category={category}
          datasetId={datasetId}
          gridCellSize={gridCellSize}
          isTnw={isTnw}
          tabKey={activeTab}
          onChangePointLocation={onChangePointLocation}
          onMeshControl={onMeshControl}
          setActivePlotValidation={setActivePlotValidation}
          setDatasetIsOfflineWarningVisible={setDatasetIsOfflineWarningVisible}
          userGroupPermissions={userGroupPermissions}
          waterDepth={waterDepth}
          activePoint={activePoint}
          point={point}
        />
      </div>
    </Modal>
  );
});

